// SCR-STAFF-102 — بخش‌های تکمیلی پرونده (AP-PAT-02): برچسب‌ها (US-PAT-060؛ بدون محتوای بالینی — PHX-PAT-0801)، VIP و پنهان‌سازی
// (US-PAT-061)، تصویر و کارت با QR امضاشده (US-PAT-005؛ ابطال کارت)، و ثبت یا بازگردانی فوت (US-PAT-064؛ نوبت‌های آینده را SCH
// لغو می‌کند).
import AddIcon from '@mui/icons-material/Add';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type PatientCardIssuedView,
  type PatientCardView,
  type PatientView,
  type TagRequest,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  ReasonDialog,
  toJalali,
  toJalaliDateTime,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { parseJalaliInput, patKeys, usePatientInvalidation, useTagDefinitions } from './common';
import { PatientPhoto } from './PatientHeader';
import { QrCode } from './QrCode';

interface TagOption {
  definitionId: string | null;
  label: string;
  color: string;
}

function TagsSection({ patient }: { patient: PatientView }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = usePatientInvalidation(patient.id);
  const write = useWriteAccess('pat.patient.tag.manage');
  const definitions = useTagDefinitions();
  const initial = (): TagOption[] => patient.tags.map((tag) => ({ definitionId: tag.definitionId, label: tag.label, color: tag.color }));
  const [value, setValue] = useState<TagOption[]>(initial);
  const [saved, setSaved] = useState(false);
  useEffect(() => setValue(patient.tags.map((tag) => ({ definitionId: tag.definitionId, label: tag.label, color: tag.color }))), [patient.tags]);
  const options: TagOption[] = (definitions.data ?? []).map((item) => ({ definitionId: item.id, label: item.name, color: item.color }));
  const save = useMutation({
    mutationFn: (tags: TagRequest[]) =>
      unwrap(tenantApi.PUT('/api/v1/patients/patients/{id}/tags', { params: { path: { id: patient.id } }, body: { tags } })),
    onMutate: () => setSaved(false),
    onSuccess: (updated) => {
      queryClient.setQueryData(patKeys.patient(session?.tenantId, patient.id), updated);
      invalidate();
      setSaved(true);
    },
  });
  const clinical = isApiError(save.error) && save.error.code === 'PHX-PAT-0801';
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="tags-section">
      <Typography variant="h2" gutterBottom>
        {t('pat:tags.title')}
      </Typography>
      <Stack spacing={1.5}>
        {saved && <Alert severity="success">{t('pat:tags.saved')}</Alert>}
        <Autocomplete<TagOption, true, false, true>
          multiple
          freeSolo
          options={options}
          value={value}
          disabled={!write.canWrite}
          onChange={(_event, next) =>
            setValue(
              next.map((item) =>
                typeof item === 'string' ? { definitionId: null, label: item.trim(), color: '#757575' } : item,
              ).filter((item, index, all) => item.label.length > 0 && all.findIndex((other) => other.label === item.label) === index),
            )
          }
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          isOptionEqualToValue={(option, selected) => option.label === selected.label}
          renderValue={(selected, getItemProps) =>
            selected.map((option, index) => {
              const { key, ...rest } = getItemProps({ index });
              const tag = typeof option === 'string' ? { label: option, color: '#757575' } : option;
              return <Chip key={key} {...rest} size="small" variant="outlined" sx={{ borderColor: tag.color }} label={tag.label} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('pat:tags.label')}
              helperText={clinical ? t('pat:tags.clinical') : t('pat:tags.help')}
              error={clinical}
              slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'tags-input', maxLength: 30 } }}
            />
          )}
        />
        {save.error && !clinical ? <ErrorState error={save.error} compact /> : null}
        {write.canWrite && (
          <div>
            <Button
              variant="contained"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  value.map((item) => ({
                    definitionId: item.definitionId,
                    text: item.definitionId ? null : item.label,
                    color: item.definitionId ? null : item.color,
                  })),
                )
              }
              data-testid="tags-save"
            >
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

function PrivacySection({ patient }: { patient: PatientView }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const invalidate = usePatientInvalidation(patient.id);
  const mask = useWriteAccess('pat.patient.mask.manage');
  const deceasedAccess = useWriteAccess('pat.patient.mark_deceased');
  const [vipReason, setVipReason] = useState<boolean | null>(null);
  const [marking, setMarking] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [date, setDate] = useState('');
  const [touched, setTouched] = useState(false);
  const toggle = useMutation({
    mutationFn: ({ masked, reason }: { masked: boolean; reason: string }) =>
      unwrap(tenantApi.PUT('/api/v1/patients/patients/{id}/mask', { params: { path: { id: patient.id } }, body: { masked, reason } })),
    onSuccess: () => {
      invalidate();
      setVipReason(null);
    },
  });
  const markDeceased = useMutation({
    mutationFn: (business: string) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}:mark-deceased', {
          params: { path: { id: patient.id } },
          body: { date: business, reason: null },
        }),
      ),
    onSuccess: () => {
      invalidate();
      setMarking(false);
    },
  });
  const revert = useMutation({
    mutationFn: (reason: string) =>
      unwrap(tenantApi.POST('/api/v1/patients/patients/{id}:revert-deceased', { params: { path: { id: patient.id } }, body: { reason } })),
    onSuccess: () => {
      invalidate();
      setReverting(false);
    },
  });
  const business = parseJalaliInput(date);
  if (!mask.permitted && !deceasedAccess.permitted) {
    return null;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="privacy-section">
      <Typography variant="h2" gutterBottom>
        {t('pat:privacy.title')}
      </Typography>
      <Stack spacing={1.5}>
        {mask.permitted && (
          <FormControlLabel
            control={
              <Switch
                checked={patient.badges.vip}
                disabled={!mask.canWrite || toggle.isPending}
                onChange={(event) => setVipReason(event.target.checked)}
                slotProps={{ input: { 'aria-describedby': 'vip-help' } }}
                data-testid="vip-switch"
              />
            }
            label={t('pat:privacy.vip')}
          />
        )}
        {mask.permitted && (
          <Typography id="vip-help" variant="body2" color="text.secondary">
            {t('pat:privacy.vip_help')}
          </Typography>
        )}
        {patient.deceasedOn && (
          <Alert severity="error" data-testid="deceased-alert">
            {t('pat:deceased.recorded', { date: toJalali(patient.deceasedOn) })}
          </Alert>
        )}
        {deceasedAccess.canWrite && (
          <div>
            {patient.deceasedOn ? (
              <Button color="inherit" onClick={() => setReverting(true)} data-testid="deceased-revert">
                {t('pat:deceased.revert')}
              </Button>
            ) : (
              <Button
                color="error"
                onClick={() => {
                  setDate('');
                  setTouched(false);
                  setMarking(true);
                }}
                data-testid="deceased-mark"
              >
                {t('pat:deceased.mark')}
              </Button>
            )}
          </div>
        )}
      </Stack>
      <ReasonDialog
        open={vipReason !== null}
        titleKey={vipReason ? 'pat:privacy.vip_on' : 'pat:privacy.vip_off'}
        messageKey="pat:privacy.vip_help"
        destructive={false}
        busy={toggle.isPending}
        error={toggle.error}
        onConfirm={(reason) => vipReason !== null && toggle.mutate({ masked: vipReason, reason })}
        onCancel={() => setVipReason(null)}
      />
      <FormDialog
        open={marking}
        titleKey="pat:deceased.mark"
        screenId="SCR-STAFF-102-DECEASED"
        onSubmit={() => {
          setTouched(true);
          if (business) {
            markDeceased.mutate(business);
          }
        }}
        onClose={() => setMarking(false)}
        isDirty={date.length > 0}
        isSubmitting={markDeceased.isPending}
        submitError={markDeceased.error}
        submitKey="pat:deceased.confirm"
      >
        <Alert severity="warning">{t('pat:deceased.warning')}</Alert>
        <TextField
          label={t('pat:deceased.date')}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          required
          error={touched && !business}
          helperText={touched && !business ? t('pat:validation.birth_date') : t('pat:fields.birth_date_help')}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'deceased-date' } }}
        />
      </FormDialog>
      <ReasonDialog
        open={reverting}
        titleKey="pat:deceased.revert"
        messageKey="pat:deceased.revert_confirm"
        busy={revert.isPending}
        error={revert.error}
        onConfirm={(reason) => revert.mutate(reason)}
        onCancel={() => setReverting(false)}
      />
    </Paper>
  );
}

function CardDialog({ issued, onClose }: { issued: PatientCardIssuedView | null; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const titleId = useId();
  return (
    <Dialog open={issued !== null} onClose={onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>{t('pat:card.title')}</DialogTitle>
      <DialogContent>
        {issued && (
          <Stack alignItems="center" spacing={1} data-testid="patient-card" className="phoenix-print-area">
            <Typography variant="subtitle1">{issued.displayName}</Typography>
            <Typography variant="body2">{t('pat:search.file_number', { number: faDigits(issued.fileNumber) })}</Typography>
            <QrCode value={issued.code} title={t('pat:card.qr')} />
            <Typography variant="caption" color="text.secondary">
              {t('pat:card.issued_at', { date: toJalaliDateTime(issued.card.issuedAt) })}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
        <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={() => window.print()} data-testid="card-print">
          {t('pat:card.print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PhotoCardSection({ patient }: { patient: PatientView }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = usePatientInvalidation(patient.id);
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess('pat.patient.update');
  const inputId = useId();
  const [issued, setIssued] = useState<PatientCardIssuedView | null>(null);
  const [revoking, setRevoking] = useState<PatientCardView | null>(null);
  const cards = useQuery({
    queryKey: patKeys.cards(session?.tenantId, patient.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/cards', { params: { path: { id: patient.id } } })),
  });
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return unwrap(
        tenantApi.PUT('/api/v1/patients/patients/{id}/photo', {
          params: { path: { id: patient.id } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patKeys.photo(session?.tenantId, patient.id) });
      invalidate();
    },
  });
  const removePhoto = useMutation({
    mutationFn: () => unwrap(tenantApi.DELETE('/api/v1/patients/patients/{id}/photo', { params: { path: { id: patient.id } } })),
    onSuccess: invalidate,
  });
  const issue = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/cards', {
          params: { path: { id: patient.id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (data) => {
      setIssued(data);
      void cards.refetch();
    },
  });
  const revoke = useMutation({
    mutationFn: ({ card, reason }: { card: PatientCardView; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/cards/{cardId}:revoke', {
          params: { path: { id: patient.id, cardId: card.id } },
          body: { reason },
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.cards(session?.tenantId, patient.id), data);
      setRevoking(null);
    },
  });
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      upload.mutate(file);
    }
  };
  const columns: DataTableColumn<PatientCardView>[] = [
    { id: 'issued', headerKey: 'pat:card.issued', cell: (row) => toJalaliDateTime(row.issuedAt) },
    {
      id: 'status',
      headerKey: 'pat:card.status_label',
      cell: (row) => <Chip size="small" color={row.status === 'Active' ? 'success' : 'default'} label={t(`pat:card.status.${row.status}`)} />,
    },
    {
      id: 'actions',
      headerKey: 'pat:card.revoke',
      cell: (row) =>
        write.canWrite && row.status === 'Active' ? (
          <Button size="small" color="error" onClick={() => setRevoking(row)}>
            {t('pat:card.revoke')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="photo-card-section">
      <Typography variant="h2" gutterBottom>
        {t('pat:card.section')}
      </Typography>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <PatientPhoto patientId={patient.id} hasPhoto={patient.hasPhoto} name={patient.displayName} size={72} />
          {write.canWrite && (
            <Stack direction="row" spacing={1}>
              <Button component="label" htmlFor={inputId} disabled={upload.isPending}>
                {t(patient.hasPhoto ? 'pat:photo.replace' : 'pat:photo.upload')}
                <Box
                  component="input"
                  id={inputId}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onFile}
                  sx={{ display: 'none' }}
                  data-testid="photo-input"
                />
              </Button>
              {patient.hasPhoto && (
                <Button color="inherit" onClick={() => removePhoto.mutate()} disabled={removePhoto.isPending}>
                  {t('pat:photo.remove')}
                </Button>
              )}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('pat:photo.help')}
          </Typography>
        </Stack>
        {upload.error ? <ErrorState error={upload.error} compact /> : null}
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {t('pat:card.title')}
          </Typography>
          {write.canWrite && (
            <Button startIcon={<AddIcon />} onClick={() => issue.mutate()} disabled={issue.isPending} data-testid="card-issue">
              {t('pat:card.issue')}
            </Button>
          )}
        </Stack>
        {issue.error ? <ErrorState error={issue.error} compact /> : null}
        <DataTable
          captionKey="pat:card.title"
          columns={columns}
          rows={cards.data?.items}
          isLoading={cards.isPending}
          error={cards.error}
          getRowId={(row) => row.id}
          emptyTitleKey="pat:card.empty"
        />
      </Stack>
      <CardDialog issued={issued} onClose={() => setIssued(null)} />
      <ReasonDialog
        open={revoking !== null}
        titleKey="pat:card.revoke"
        messageKey="pat:card.revoke_confirm"
        busy={revoke.isPending}
        error={revoke.error}
        onConfirm={(reason) => revoking && revoke.mutate({ card: revoking, reason })}
        onCancel={() => setRevoking(null)}
      />
    </Paper>
  );
}

/**
 * گردشگر سلامت (US-PAT-062؛ AC-PAT-062.1): کشور مبدأ، زبان، هماهنگ‌کننده یا آژانس، تاریخ ورود و خروج و ارز ترجیحی.
 * BIL ارز ترجیحی را برای پیشنهاد ارز صورتحساب و CAT برای فهرست قیمت ارزی می‌خواند (BR-CAT-025).
 */
function HealthTouristSection({ patient }: { patient: PatientView }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const invalidate = usePatientInvalidation(patient.id);
  const write = useWriteAccess('pat.patient.update');
  const current = patient.healthTourist ?? null;
  const [form, setForm] = useState({
    countryCode: current?.countryCode ?? '',
    languageCode: current?.languageCode ?? '',
    coordinator: current?.coordinator ?? '',
    entryDate: current?.entryDate ?? '',
    exitDate: current?.exitDate ?? '',
    preferredCurrency: current?.preferredCurrency ?? '',
  });
  const [failure, setFailure] = useState<unknown>();

  const save = useMutation({
    mutationFn: (clear: boolean) =>
      unwrap(
        tenantApi.PUT('/api/v1/patients/patients/{id}/health-tourist', {
          params: { path: { id: patient.id } },
          body: clear
            ? { countryCode: null, languageCode: null, coordinator: null, entryDate: null, exitDate: null, preferredCurrency: null }
            : {
                countryCode: form.countryCode.trim() || null,
                languageCode: form.languageCode.trim() || null,
                coordinator: form.coordinator.trim() || null,
                entryDate: form.entryDate || null,
                exitDate: form.exitDate || null,
                preferredCurrency: form.preferredCurrency.trim() || null,
              },
        }),
      ),
    onMutate: () => setFailure(undefined),
    onSuccess: () => invalidate(),
    onError: setFailure,
  });

  const field = (key: keyof typeof form, labelKey: string, extra: Record<string, unknown> = {}) => (
    <TextField
      size="small"
      label={t(labelKey)}
      value={form[key]}
      onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
      disabled={!write.canWrite || save.isPending}
      slotProps={{ htmlInput: { 'data-testid': `tourist-${key}`, ...extra } as object }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="patient-health-tourist">
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">{t('pat:tourist.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pat:tourist.help')}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {field('countryCode', 'pat:tourist.country', { maxLength: 2 })}
          {field('languageCode', 'pat:tourist.language', { maxLength: 8 })}
          {field('coordinator', 'pat:tourist.coordinator', { maxLength: 200 })}
          {field('preferredCurrency', 'pat:tourist.currency', { maxLength: 3 })}
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {field('entryDate', 'pat:tourist.entry_date', { placeholder: '2026-11-01' })}
          {field('exitDate', 'pat:tourist.exit_date', { placeholder: '2026-11-20' })}
          <Button
            variant="contained"
            disabled={!write.canWrite || save.isPending}
            onClick={() => save.mutate(false)}
            data-testid="tourist-save"
          >
            {t('common:actions.save')}
          </Button>
          {current && (
            <Button
              color="error"
              disabled={!write.canWrite || save.isPending}
              onClick={() => {
                setForm({ countryCode: '', languageCode: '', coordinator: '', entryDate: '', exitDate: '', preferredCurrency: '' });
                save.mutate(true);
              }}
              data-testid="tourist-clear"
            >
              {t('pat:tourist.clear')}
            </Button>
          )}
        </Stack>
        {failure !== undefined && <ErrorState error={failure} />}
      </Stack>
    </Paper>
  );
}

export function PatientExtrasPanel({ patient }: { patient: PatientView }) {
  return (
    <Stack spacing={2}>
      <TagsSection patient={patient} />
      <PrivacySection patient={patient} />
      <PhotoCardSection patient={patient} />
      <HealthTouristSection patient={patient} />
    </Stack>
  );
}
